import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// Language mappings
const languageMap = {
  'E': 'English',
  'H': 'Hindi',
  'M': 'Marathi',
  'G': 'Gujarati'
};

// Multilingual text translations
const translations = {
  prescription: {
    'E': 'Medical Prescription',
    'H': 'चिकित्सा नुस्खा',
    'M': 'वैद्यकीय नुस्खा',
    'G': 'તબીબી પ્રિસ્ક્રિપ્શન'
  },
  date: {
    'E': 'Date',
    'H': 'दिनांक',
    'M': 'दिनांक',
    'G': 'તારીખ'
  },
  patientInfo: {
    'E': 'Patient Information',
    'H': 'रोगी की जानकारी',
    'M': 'रुग्णाची माहिती',
    'G': 'દર્દીની માહિતી'
  },
  name: {
    'E': 'Name',
    'H': 'नाम',
    'M': 'नाव',
    'G': 'નામ'
  },
  gender: {
    'E': 'Gender',
    'H': 'लिंग',
    'M': 'लिंग',
    'G': 'લિંગ'
  },
  phone: {
    'E': 'Phone',
    'H': 'फोन',
    'M': 'फोन',
    'G': 'ફોન'
  },
  age: {
    'E': 'Age',
    'H': 'आयु',
    'M': 'वय',
    'G': 'ઉંમર'
  },
  medications: {
    'E': 'Medications',
    'H': 'दवाइयाँ',
    'M': 'औषधे',
    'G': 'દવાઓ'
  },
  tests: {
    'E': 'Tests',
    'H': 'जांचें',
    'M': 'चाचण्या',
    'G': 'પરીક્ષણો'
  },
  imagingTests: {
    'E': 'Imaging Tests',
    'H': 'इमेजिंग जांचें',
    'M': 'इमेजिंग चाचण्या',
    'G': 'ઇમેજિંગ પરીક્ષણો'
  },
  instruction: {
    'E': 'Instructions',
    'H': 'निर्देश',
    'M': 'सूचना',
    'G': 'સૂચનાઓ'
  },
  days: {
    'E': 'Days',
    'H': 'दिन',
    'M': 'दिवस',
    'G': 'દિવસો'
  },
  foodAdvice: {
    'E': 'Food Advice',
    'H': 'भोजन सलाह',
    'M': 'आहार सल्ला',
    'G': 'ખોરાકની સલાહ'
  },
  doctorSignature: {
    'E': "Doctor's Signature",
    'H': 'डॉक्टर के हस्ताक्षर',
    'M': 'डॉक्टरांची स्वाक्षरी',
    'G': 'ડૉક્ટરની સહી'
  },
  duplicateCopy: {
    'E': 'DUPLICATE COPY',
    'H': 'डुप्लिकेट कॉपी',
    'M': 'डुप्लिकेट कॉपी',
    'G': 'ડુપ્લિકેટ કૉપી'
  }
};

// Get translated text
const getTranslation = (key, language = 'E') => {
  return translations[key]?.[language] || translations[key]?.['E'] || key;
};

// Format timing slots to readable string (Morning, Noon, Evening, Night)
const formatTiming = (timing = {}) => {
  const slots = [];
  if (timing.morning) slots.push('Morning');
  if (timing.noon) slots.push('Noon');
  if (timing.evening) slots.push('Evening');
  if (timing.night) slots.push('Night');
  return slots.join(', ');
};

// Resolve test names by id from master data records
const resolveTestNames = (tests = [], testsRecords = []) => {
  if (!tests || tests.length === 0) return [];

  // If tests are already objects with name property, return their names
  return tests.map(test => {
    if (typeof test === 'object' && test !== null) {
      // Test is already a populated object
      return test.name || 'Unknown Test';
    }
    // Test is an ID, try to find it in testsRecords
    const found = (testsRecords || []).find(t => (t._id || t.id) === test);
    return found?.name || String(test);
  });
};

// Resolve imaging test data with modality, views, and instructions
const resolveImagingTestData = (imagingTests = [], imagingTestsRecords = []) => {
  if (!imagingTests || imagingTests.length === 0) return [];

  return imagingTests.map(item => {
    // Handle new format: {test: object/id, modality, selectedViews, instructions}
    const test = item.test || item;
    const itemModality = item.modality || '';
    const selectedViews = item.selectedViews || [];
    const instructions = item.instructions || '';

    if (typeof test === 'object' && test !== null) {
      // Test is already a populated object
      return {
        modality: itemModality || test.modality || '—',
        testName: test.testName || test.name || 'Unknown Imaging Test',
        views: selectedViews,
        instructions: instructions || test.specialInstructions || ''
      };
    }

    // Test is an ID, try to find it in imagingTestsRecords
    const found = (imagingTestsRecords || []).find(t => (t._id || t.id) === test);
    if (found) {
      return {
        modality: itemModality || found.modality || '—',
        testName: found.testName || found.name || 'Unknown Imaging Test',
        views: selectedViews,
        instructions: instructions || found.specialInstructions || ''
      };
    }

    return {
      modality: itemModality || '—',
      testName: String(test),
      views: selectedViews,
      instructions: instructions
    };
  });
};


const normalizeGenderToken = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'object') {
    const objectCandidates = [value.label, value.name, value.value, value.display];
    for (const candidate of objectCandidates) {
      const resolved = normalizeGenderToken(candidate);
      if (resolved) return resolved;
    }
    return null;
  }

  const token = String(value).trim();
  if (!token) return null;

  const lowered = token.toLowerCase();
  if (['m', 'male', 'm.', 'man'].includes(lowered)) return 'Male';
  if (['f', 'female', 'f.', 'woman'].includes(lowered)) return 'Female';
  if (['o', 'other', 'others', 'non-binary', 'nonbinary', 'nb'].includes(lowered)) return 'Other';

  return token.charAt(0).toUpperCase() + token.slice(1);
};

const formatGenderValue = (...candidates) => {
  for (const candidate of candidates) {
    const formatted = normalizeGenderToken(candidate);
    if (formatted) return formatted;
  }
  return 'N/A';
};

const calculateAgeFromDOB = (dobValue) => {
  if (!dobValue) return null;
  const dob = new Date(dobValue);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
};

const pickFirstDefined = (values = []) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return undefined;
};

const resolveAgeText = (patientData = {}) => {
  console.log('[PDFGEN][resolveAgeText] incoming patientData keys:', Object.keys(patientData || {}));
  const ageCandidate = pickFirstDefined([
    patientData.age,
    patientData.ageYears,
    patientData.ageYear,
    patientData.patientAge,
    patientData.basicInfo?.age,
    patientData.ageInfo
  ]);

  if (ageCandidate !== undefined) {
    console.log('[PDFGEN][resolveAgeText] raw ageCandidate value:', ageCandidate);
    if (typeof ageCandidate === 'object' && ageCandidate !== null) {
      const years = pickFirstDefined([ageCandidate.years, ageCandidate.year, ageCandidate.yrs, ageCandidate.value]);
      const months = pickFirstDefined([ageCandidate.months, ageCandidate.month, ageCandidate.mos]);
      const parts = [];
      if (!Number.isNaN(Number(years)) && years !== undefined && years !== null) {
        parts.push(`${Number(years)} yrs`);
      }
      if (!Number.isNaN(Number(months)) && months !== undefined && months !== null) {
        parts.push(`${Number(months)} mos`);
      }
      if (parts.length) return parts.join(' ');
      const display = pickFirstDefined([ageCandidate.display, ageCandidate.label]);
      if (display !== undefined) return String(display);
    } else {
      const raw = String(ageCandidate).trim();
      if (/^\d+(\.\d+)?$/.test(raw)) return `${raw} yrs`;
      return raw;
    }
  }

  const dobCandidate = pickFirstDefined([
    patientData.dob,
    patientData.dateOfBirth,
    patientData.date_of_birth,
    patientData.birthDate,
    patientData.basicInfo?.dob
  ]);
  console.log('[PDFGEN][resolveAgeText] dobCandidate:', dobCandidate);
  const computedAge = calculateAgeFromDOB(dobCandidate);
  console.log('[PDFGEN][resolveAgeText] computedAge:', computedAge);
  if (computedAge !== null) return `${computedAge} yrs`;

  return 'N/A';
};

// Generate PDF prescription
export const generatePrescriptionPDF = async (prescriptionData, patientData, selectedSlogan, isDuplicate = false, options = {}) => {
  try {
    // Double-check duplicate flag from prescription data as backup
    const duplicateFromData = prescriptionData?.flags?.duplicatePrint;
    const finalIsDuplicate = isDuplicate || duplicateFromData;

    // Validate inputs
    if (!prescriptionData) throw new Error('Prescription data is required');
    if (!patientData) throw new Error('Patient data is required');

    const language = prescriptionData.language || 'E';

    // For non-English languages, use HTML to Canvas approach for better Unicode support
    if (language !== 'E') {
      return await generatePrescriptionPDFFromHTML(prescriptionData, patientData, selectedSlogan, finalIsDuplicate, options);
    }

    // For English, use jsPDF directly
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPosition = margin;

    // Prepare Data
    const doctorData = options.doctorData || null;
    const patientFullName = [patientData?.firstName, patientData?.lastName].filter(Boolean).join(' ').trim() || 'N/A';
    const resolvedGender = formatGenderValue(patientData?.sex, patientData?.gender, patientData?.genderDisplay, patientData?.genderIdentity);
    const phoneCandidate = pickFirstDefined([
      patientData?.mobile, patientData?.phone, patientData?.contactNumber,
      patientData?.basicInfo?.mobile, patientData?.basicInfo?.phone
    ]);
    const phoneDisplay = phoneCandidate !== undefined ? String(phoneCandidate).trim() : 'N/A';
    const ageText = resolveAgeText(patientData);
    // Construct doctor name: prefer firstName + lastName, fallback to name field
    let doctorFullName = 'N/A';
    if (doctorData?.firstName || doctorData?.lastName) {
      doctorFullName = [doctorData.firstName, doctorData.lastName].filter(Boolean).join(' ').trim();
    } else if (doctorData?.name) {
      doctorFullName = doctorData.name;
    }
    const docNameFull = `Dr. ${doctorFullName}`;

    // --- Helpers ---

    const drawDuplicateWatermark = () => {
      if (!finalIsDuplicate) return;
      const watermarkText = getTranslation('duplicateCopy', language);
      pdf.saveGraphicsState();
      // Darker, bold watermark for duplicate prints
      pdf.setTextColor(180, 180, 180); // Dark gray
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(68);
      const angle = 45;
      try {
        pdf.text(watermarkText, 40, pageHeight - 60, { angle: angle, align: 'left' });
      } catch (e) {
        pdf.text(watermarkText, 40, pageHeight - 60);
      }
      pdf.restoreGraphicsState();
    };

    const drawHeader = () => {
      yPosition = 15;
      // Hospital Name
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('HealthCare Plus Hospital', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 6;

      // Address & Contact
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('123 Medical Center Drive, Healthcare City | +1-800-HEALTH', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 4;

      // Line
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(0, 0, 0);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;
    };

    const drawPatientDoctorDetails = () => {
      const startY = yPosition;

      // Patient Details (Left)
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Patient Details :', margin, startY);

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      let leftY = startY + 6;
      pdf.text(patientFullName, margin, leftY);
      leftY += 5;
      pdf.text(`${ageText} / ${resolvedGender}`, margin, leftY);
      leftY += 5;
      pdf.text(`Ph: ${phoneDisplay}`, margin, leftY);
      leftY += 5;
      if (patientData?.email) {
        pdf.text(`Email: ${patientData.email}`, margin, leftY);
        leftY += 5;
      }

      // Doctor Details (Right)
      const rightX = pageWidth / 2 + 20;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Doctor Details :', rightX, startY);

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      let rightY = startY + 6;
      pdf.text(docNameFull, rightX, rightY);
      rightY += 5;
      if (doctorData?.specialization) {
        pdf.text(doctorData.specialization, rightX, rightY);
        rightY += 5;
      }
      if (doctorData?.qualification) {
        pdf.text(String(doctorData.qualification), rightX, rightY);
        rightY += 5;
      }
      if (doctorData?.licenseNumber) {
        pdf.text(`Lic: ${doctorData.licenseNumber}`, rightX, rightY);
        rightY += 5;
      }
      if (doctorData?.registrationNumber) {
        pdf.text(`Reg: ${doctorData.registrationNumber}`, rightX, rightY);
        rightY += 5;
      }

      // Use the larger of the two columns to determine final yPosition
      yPosition = Math.max(leftY, rightY) + 5;
    };

    const checkAddPage = (heightNeeded) => {
      // Reserve space for footer (Signature + Page No) -> approx 35mm
      if (yPosition + heightNeeded > pageHeight - 35) {
        pdf.addPage();
        drawDuplicateWatermark();
        drawHeader();
        return true;
      }
      return false;
    };

    // --- Start Generation ---

    // Page 1 Setup
    drawDuplicateWatermark();
    drawHeader();

    // Appointment Date/Time (at top for visibility)
    if (prescriptionData.appointmentDate || prescriptionData.dateTime) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text('Appointment :', margin, yPosition);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const appointmentDateTime = prescriptionData.appointmentDate || prescriptionData.dateTime;
      const formattedDateTime = new Date(appointmentDateTime).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      pdf.text(formattedDateTime, margin + 30, yPosition);
      yPosition += 10;
    }

    drawPatientDoctorDetails();

    // Vitals (if available)
    if (prescriptionData.vitals && Object.keys(prescriptionData.vitals).length > 0) {
      checkAddPage(25);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Vital Signs :', margin, yPosition);
      yPosition += 6;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const vitals = prescriptionData.vitals;
      const vitalEntries = [];

      if (vitals.bpSys && vitals.bpDia) vitalEntries.push(`BP: ${vitals.bpSys}/${vitals.bpDia} mmHg`);
      if (vitals.pulse) vitalEntries.push(`Pulse: ${vitals.pulse} bpm`);
      if (vitals.temperature) vitalEntries.push(`Temp: ${vitals.temperature}°F`);
      if (vitals.oxygenSaturation) vitalEntries.push(`SpO2: ${vitals.oxygenSaturation}%`);
      if (vitals.respiration) vitalEntries.push(`RR: ${vitals.respiration}/min`);
      if (vitals.heightCm) vitalEntries.push(`Height: ${vitals.heightCm} cm`);
      if (vitals.weightKg) vitalEntries.push(`Weight: ${vitals.weightKg} kg`);
      if (vitals.bmi) vitalEntries.push(`BMI: ${Number(vitals.bmi).toFixed(2)}`);

      // Display vitals in a grid (2 columns)
      const col1X = margin;
      const col2X = pageWidth / 2;
      let rowY = yPosition;

      vitalEntries.forEach((entry, idx) => {
        const xPos = idx % 2 === 0 ? col1X : col2X;
        if (idx % 2 === 0 && idx > 0) rowY += 5;
        pdf.text(entry, xPos, rowY);
      });

      yPosition = rowY + 8;
    }

    // 1. Complaint
    if (prescriptionData.chiefComplaint) {
      checkAddPage(20);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Complaint :', margin, yPosition);

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      // Split by newlines first to preserve user's line breaks
      const complaintLines = prescriptionData.chiefComplaint.split('\n');
      complaintLines.forEach((line, idx) => {
        if (line.trim()) {
          const wrappedLines = pdf.splitTextToSize(line, pageWidth - (margin * 2) - 30);
          pdf.text(wrappedLines, margin + 30, yPosition);
          yPosition += (wrappedLines.length * 5);
        }
        if (idx < complaintLines.length - 1) yPosition += 2; // Small gap between user's lines
      });
      yPosition += 6;
    }

    // 2. Examination Finding
    if (prescriptionData.physicalExaminationFindings) {
      checkAddPage(20);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Examination Finding :', margin, yPosition);

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      // Split by newlines first to preserve user's line breaks
      const examLines = prescriptionData.physicalExaminationFindings.split('\n');
      examLines.forEach((line, idx) => {
        if (line.trim()) {
          const wrappedLines = pdf.splitTextToSize(line, pageWidth - (margin * 2) - 45);
          pdf.text(wrappedLines, margin + 45, yPosition);
          yPosition += (wrappedLines.length * 5);
        }
        if (idx < examLines.length - 1) yPosition += 2; // Small gap between user's lines
      });
      yPosition += 6;
    }

    // Known Case Disease (inline style)
    if (prescriptionData.knownCaseDisease) {
      checkAddPage(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text('Known Disease :', margin, yPosition);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const diseaseText = typeof prescriptionData.knownCaseDisease === 'object'
        ? prescriptionData.knownCaseDisease.name || JSON.stringify(prescriptionData.knownCaseDisease)
        : prescriptionData.knownCaseDisease;
      // Split by newlines first to preserve user's line breaks
      const diseaseLines = String(diseaseText).split('\n');
      diseaseLines.forEach((line, idx) => {
        if (line.trim()) {
          const wrappedLines = pdf.splitTextToSize(line, pageWidth - (margin * 2) - 35);
          pdf.text(wrappedLines, margin + 35, yPosition);
          yPosition += (wrappedLines.length * 5);
        }
        if (idx < diseaseLines.length - 1) yPosition += 2; // Small gap between user's lines
      });
      yPosition += 6;
    }

    // 3. Medicine (Table)
    if (prescriptionData.items && prescriptionData.items.length > 0) {
      checkAddPage(20);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Medicine :', margin, yPosition);
      yPosition += 6;

      // Table Header
      const cols = [
        { header: 'Medicine Name', width: 50 },
        { header: 'Dosage', width: 25 },
        { header: 'Frequency', width: 25 },
        { header: 'Route', width: 20 },
        { header: 'Duration', width: 20 },
        { header: 'Instruction', width: 40 }
      ];
      const rowHeight = 8;

      // Draw Header Row
      checkAddPage(rowHeight);
      let currentX = margin;
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPosition - 4, pageWidth - (margin * 2), rowHeight, 'F');
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');

      cols.forEach(col => {
        pdf.text(col.header, currentX + 2, yPosition + 1);
        currentX += col.width;
      });
      yPosition += rowHeight;

      // Draw Rows
      pdf.setFont('helvetica', 'normal');
      prescriptionData.items.forEach(item => {
        if (!item.name && !item.medicine) return;

        const medicineObj = typeof item.medicine === 'object' ? item.medicine : {};
        const name = item.name || medicineObj.name || '';
        const dose = item.dose || item.doseQuantity || medicineObj.doseQuantity || '';
        // Compute frequency from timing slots like PatientDetails.jsx does
        const timingLabel = formatTiming(item.timing || medicineObj.timing || {});
        const freq = item.frequency || timingLabel || (typeof medicineObj.dosesPerDay === 'number'
          ? `${medicineObj.dosesPerDay} per day`
          : '');
        const route = item.route || medicineObj.route || 'Oral';
        const dur = item.days || medicineObj.days || '';
        const instr = item.instruction || medicineObj.instruction || '';

        const nameLines = pdf.splitTextToSize(name, cols[0].width - 4);
        const doseLines = pdf.splitTextToSize(dose, cols[1].width - 4);
        const freqLines = pdf.splitTextToSize(freq, cols[2].width - 4);
        const routeLines = pdf.splitTextToSize(route, cols[3].width - 4);
        const durLines = pdf.splitTextToSize(dur, cols[4].width - 4);
        const instrLines = pdf.splitTextToSize(instr, cols[5].width - 4);

        const maxLines = Math.max(nameLines.length, doseLines.length, freqLines.length, routeLines.length, durLines.length, instrLines.length);
        const thisRowHeight = (maxLines * 5) + 4;

        checkAddPage(thisRowHeight);

        let rX = margin;
        pdf.text(nameLines, rX + 2, yPosition);
        rX += cols[0].width;
        pdf.text(doseLines, rX + 2, yPosition);
        rX += cols[1].width;
        pdf.text(freqLines, rX + 2, yPosition);
        rX += cols[2].width;
        pdf.text(routeLines, rX + 2, yPosition);
        rX += cols[3].width;
        pdf.text(durLines, rX + 2, yPosition);
        rX += cols[4].width;
        pdf.text(instrLines, rX + 2, yPosition);

        yPosition += thisRowHeight;

        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, yPosition - 2, pageWidth - margin, yPosition - 2);
      });
      yPosition += 6;
    }

    // 4. Imaging Test (Table)
    const imagingTestData = resolveImagingTestData(prescriptionData.imagingTests, options.imagingTestsRecords);
    if (imagingTestData && imagingTestData.length > 0) {
      checkAddPage(25);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Imaging Test :', margin, yPosition);
      yPosition += 6;

      // Table column widths
      const tableWidth = pageWidth - (margin * 2);
      const col1 = margin + 2; // #
      const col2 = col1 + 10; // Modality
      const col3 = col2 + 35; // Test Name
      const col4 = col3 + 50; // Views
      const col5 = col4 + 30; // Instructions

      // Header row
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPosition - 4, tableWidth, 8, 'F');
      pdf.setFontSize(9);
      pdf.text('#', col1, yPosition + 1);
      pdf.text('Modality', col2, yPosition + 1);
      pdf.text('Test Name', col3, yPosition + 1);
      pdf.text('Views', col4, yPosition + 1);
      pdf.text('Instructions', col5, yPosition + 1);
      yPosition += 8;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);

      imagingTestData.forEach((item, idx) => {
        const rowStartY = yPosition;
        let maxRowHeight = 5;

        // Split text for wrapping
        const testLines = pdf.splitTextToSize(item.testName || '—', 48);
        const viewsText = item.views && item.views.length > 0 ? item.views.join(', ') : '—';
        const viewsLines = pdf.splitTextToSize(viewsText, 28);
        const instructionsLines = pdf.splitTextToSize(item.instructions || '—', 45);

        // Calculate row height
        maxRowHeight = Math.max(
          testLines.length * 4,
          viewsLines.length * 4,
          instructionsLines.length * 4,
          5
        ) + 2;

        checkAddPage(maxRowHeight);

        // Draw row content
        pdf.text(`${idx + 1}`, col1, yPosition + 3);
        pdf.text(item.modality || '—', col2, yPosition + 3);
        pdf.text(testLines, col3, yPosition + 3);
        pdf.text(viewsLines, col4, yPosition + 3);
        pdf.text(instructionsLines, col5, yPosition + 3);

        yPosition += maxRowHeight;

        // Draw bottom border
        pdf.setDrawColor(220, 220, 220);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      });

      yPosition += 6;
    }

    // 5. Test (Table)
    const testNames = resolveTestNames(prescriptionData.tests, options.testsRecords);
    if (testNames && testNames.length > 0) {
      checkAddPage(20);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Test :', margin, yPosition);
      yPosition += 6;

      // Header
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPosition - 4, pageWidth - (margin * 2), 8, 'F');
      pdf.setFontSize(9);
      pdf.text('Test Name', margin + 2, yPosition + 1);
      yPosition += 8;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      testNames.forEach((name, idx) => {
        const lines = pdf.splitTextToSize(`${idx + 1}. ${name}`, pageWidth - (margin * 2) - 4);
        const h = (lines.length * 4) + 3;
        checkAddPage(h);
        pdf.text(lines, margin + 2, yPosition);
        yPosition += h;
      });
      yPosition += 6;
    }

    // 6. Food Advice
    if (prescriptionData.foodAdvice) {
      checkAddPage(20);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Food Advice :', margin, yPosition);
      yPosition += 6;

      pdf.setFont('helvetica', 'normal');
      // Split by newlines first to preserve user's line breaks
      const foodLines = prescriptionData.foodAdvice.split('\n');
      foodLines.forEach((line, idx) => {
        if (line.trim()) {
          const wrappedLines = pdf.splitTextToSize(line, pageWidth - (margin * 2));
          pdf.text(wrappedLines, margin, yPosition);
          yPosition += (wrappedLines.length * 5);
        }
        if (idx < foodLines.length - 1) yPosition += 2; // Small gap between user's lines
      });
      yPosition += 6;
    }

    // 7. Slogan
    if (selectedSlogan?.slogan) {
      checkAddPage(20);
      yPosition += 5;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'italic');
      pdf.text(selectedSlogan.slogan, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
    }

    // 8. Ending Stars
    checkAddPage(10);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text('****************', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // --- Footer Loop ---
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);

      // Doctor Signature (Bottom Right)
      const sigY = pageHeight - 20;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      // Label with a clear line to its right (no overlap)
      const sigLabel = "Doctor's Signature";
      const labelWidth = pdf.getTextWidth(sigLabel);
      const lineEndX = pageWidth - margin;
      const lineStartX = lineEndX - 60;
      const labelX = lineStartX - 4; // small gap before the line
      pdf.text(sigLabel, labelX, sigY, { align: 'right' });
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(0, 0, 0);
      pdf.line(lineStartX, sigY, lineEndX, sigY);
      // Doctor name under the signature line
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(docNameFull, pageWidth - margin, sigY + 8, { align: 'right' });

      // Page Number (Bottom Center)
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }

    // Generate filename
    const firstName = (patientData?.firstName || 'patient').replace(/\s+/g, '_');
    const lastName = (patientData?.lastName || '').replace(/\s+/g, '_');
    const patientName = lastName ? `${firstName}_${lastName}` : firstName;
    const fileName = `prescription_${patientName}_${finalIsDuplicate ? 'DUPLICATE_' : ''}${new Date().toISOString().split('T')[0]}.pdf`;

    // Save PDF
    pdf.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating PDF:', error);
    return { success: false, error: error.message };
  }
};

// Generate print-friendly HTML - Matches PDF download format exactly
export const generatePrintHTML = (
  prescriptionData,
  patientData,
  selectedSlogan,
  isDuplicate = false,
  options = {}
) => {
  const language = prescriptionData.language || 'E';
  const doctorData = options.doctorData || null;

  // Construct doctor name: prefer firstName + lastName, fallback to name field
  let doctorFullName = 'N/A';
  if (doctorData?.firstName || doctorData?.lastName) {
    doctorFullName = [doctorData.firstName, doctorData.lastName].filter(Boolean).join(' ').trim();
  } else if (doctorData?.name) {
    doctorFullName = doctorData.name;
  }

  const patientFullName =
    [patientData?.firstName, patientData?.lastName].filter(Boolean).join(' ').trim() ||
    'N/A';

  const resolvedGender = formatGenderValue(
    patientData?.sex,
    patientData?.gender,
    patientData?.genderDisplay,
    patientData?.genderIdentity
  );

  const phoneCandidate = pickFirstDefined([
    patientData?.mobile,
    patientData?.phone,
    patientData?.contactNumber,
    patientData?.basicInfo?.mobile,
    patientData?.basicInfo?.phone,
  ]);
  const phoneDisplay = phoneCandidate !== undefined ? String(phoneCandidate).trim() : 'N/A';

  const ageText = resolveAgeText(patientData);

  // Font + lang attribute - Match PDF
  let fontFamily = 'Helvetica, Arial, sans-serif';
  let langAttr = 'en';

  if (language === 'H') {
    fontFamily = '"Noto Sans Devanagari", "Mangal", "Kruti Dev 010", Arial, sans-serif';
    langAttr = 'hi';
  } else if (language === 'M') {
    fontFamily = '"Noto Sans Devanagari", "Shivaji01", "Kruti Dev 010", Arial, sans-serif';
    langAttr = 'mr';
  } else if (language === 'G') {
    fontFamily = '"Noto Sans Gujarati", "Shruti", Arial, sans-serif';
    langAttr = 'gu';
  }

  // Check duplicate flag
  const duplicateFromData = prescriptionData?.flags?.duplicatePrint;
  const finalIsDuplicate = isDuplicate || duplicateFromData;

  // Vital signs - Match PDF format
  let vitalSignsHTML = '';
  const vitals = prescriptionData.vitals || {};
  const vitalEntries = [];

  if (vitals.bpSys && vitals.bpDia) vitalEntries.push(`BP: ${vitals.bpSys}/${vitals.bpDia} mmHg`);
  if (vitals.pulse) vitalEntries.push(`Pulse: ${vitals.pulse} bpm`);
  if (vitals.temperature) vitalEntries.push(`Temp: ${vitals.temperature}°F`);
  if (vitals.oxygenSaturation) vitalEntries.push(`SpO2: ${vitals.oxygenSaturation}%`);
  if (vitals.respiration) vitalEntries.push(`RR: ${vitals.respiration}/min`);
  if (vitals.heightCm) vitalEntries.push(`Height: ${vitals.heightCm} cm`);
  if (vitals.weightKg) vitalEntries.push(`Weight: ${vitals.weightKg} kg`);
  if (vitals.bmi) vitalEntries.push(`BMI: ${Number(vitals.bmi).toFixed(2)}`);

  if (vitalEntries.length > 0) {
    vitalSignsHTML = `
      <div class="section">
        <div class="section-title">Vital Signs :</div>
        <div class="vitals-grid">
          ${vitalEntries.map(entry => `<div>${entry}</div>`).join('')}
        </div>
      </div>
    `;
  }

  // Complaint & Examination - render side-by-side when both exist
  let complaintExamHTML = '';
  const hasComplaint = Boolean(prescriptionData.chiefComplaint);
  const hasExam = Boolean(prescriptionData.physicalExaminationFindings);
  if (hasComplaint && hasExam) {
    // Stack Examination on the next line, not side-by-side
    complaintExamHTML = `
      <div class="section">
        <div class="inline-box">
          <div class="section-title">Complaint :</div>
          <div class="section-content" style="white-space: pre-wrap;">${prescriptionData.chiefComplaint}</div>
        </div>
        <div class="inline-box" style="margin-top: 8pt;">
          <div class="section-title">Examination Finding :</div>
          <div class="section-content" style="white-space: pre-wrap;">${prescriptionData.physicalExaminationFindings}</div>
        </div>
      </div>
    `;
  } else if (hasComplaint) {
    complaintExamHTML = `
      <div class="section">
        <div class="inline-box">
          <div class="section-title">Complaint :</div>
          <div class="section-content" style="white-space: pre-wrap;">${prescriptionData.chiefComplaint}</div>
        </div>
      </div>
    `;
  } else if (hasExam) {
    complaintExamHTML = `
      <div class="section">
        <div class="inline-box">
          <div class="section-title">Examination Finding :</div>
          <div class="section-content" style="white-space: pre-wrap;">${prescriptionData.physicalExaminationFindings}</div>
        </div>
      </div>
    `;
  }

  // Medicines - Match PDF table format exactly
  const medicationRows = (prescriptionData.items || [])
    .filter((item) => {
      const hasName = item.name || (typeof item.medicine === 'object' && item.medicine?.name);
      return hasName;
    })
    .map((item) => {
      const medicineObj = typeof item.medicine === 'object' && item.medicine ? item.medicine : {};
      const medicineName = (typeof item.medicine === 'object' && item.medicine?.name) || item.name || 'N/A';

      const doseQuantity = item.doseQuantity || medicineObj.doseQuantity || '';
      const dosesPerDay = item.dosesPerDay || medicineObj.dosesPerDay || '';
      const doseText = item.dose || (doseQuantity && dosesPerDay ? `${doseQuantity}mg x ${dosesPerDay}` : '');

      // Compute frequency from timing slots like PatientDetails.jsx does
      const timingLabel = formatTiming(item.timing || medicineObj.timing || {});
      const frequencyText = item.frequency || timingLabel || (typeof medicineObj.dosesPerDay === 'number'
        ? `${medicineObj.dosesPerDay} per day`
        : '');
      const routeText = item.route || medicineObj.route || 'Oral';
      const daysText = item.days || item.durationDays || medicineObj.days || '';
      const instructionText = item.instruction || medicineObj.instruction || '';

      return `
        <tr>
          <td>${medicineName}</td>
          <td>${doseText || '—'}</td>
          <td>${frequencyText || '—'}</td>
          <td>${routeText}</td>
          <td>${daysText || '—'}</td>
          <td>${instructionText || '—'}</td>
        </tr>
      `;
    })
    .join('');

  const medicationsHTML = medicationRows
    ? `
      <div class="section">
        <div class="section-title">Medicine :</div>
        <table class="med-table">
          <thead>
            <tr>
              <th style="width: 30%;">Medicine Name</th>
              <th style="width: 15%;">Dosage</th>
              <th style="width: 15%;">Frequency</th>
              <th style="width: 10%;">Route</th>
              <th style="width: 10%;">Duration</th>
              <th style="width: 20%;">Instruction</th>
            </tr>
          </thead>
          <tbody>
            ${medicationRows}
          </tbody>
        </table>
      </div>
    `
    : '';

  // Imaging tests - Match PDF format
  const imagingTestData = resolveImagingTestData(
    prescriptionData.imagingTests,
    options.imagingTestsRecords
  );
  const imagingTestsHTML = imagingTestData && imagingTestData.length > 0
    ? `
      <div class="section">
        <div class="section-title">Imaging Test :</div>
        <table class="img-table">
          <thead>
            <tr>
              <th style="width: 8%;">#</th>
              <th style="width: 18%;">Modality</th>
              <th style="width: 32%;">Test Name</th>
              <th style="width: 20%;">Views</th>
              <th style="width: 22%;">Instructions</th>
            </tr>
          </thead>
          <tbody>
            ${imagingTestData.map((item, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td>${item.modality || '—'}</td>
                <td>${item.testName || '—'}</td>
                <td>${item.views && item.views.length > 0 ? item.views.join(', ') : '—'}</td>
                <td>${item.instructions || '—'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `
    : '';

  // Lab tests - Match PDF format
  const testNames = resolveTestNames(prescriptionData.tests, options.testsRecords);
  const testsHTML = testNames && testNames.length > 0
    ? `
      <div class="section">
        <div class="section-title">Test :</div>
        <table class="test-table">
          <thead>
            <tr>
              <th>Test Name</th>
            </tr>
          </thead>
          <tbody>
            ${testNames.map((name, idx) => `<tr><td>${idx + 1}. ${name}</td></tr>`).join('')}
          </tbody>
        </table>
      </div>
    `
    : '';

  // Food advice - Match PDF format
  const foodAdviceHTML = prescriptionData.foodAdvice
    ? `
      <div class="section">
        <div class="section-title">Food Advice :</div>
        <div class="section-content" style="white-space: pre-wrap;">${prescriptionData.foodAdvice}</div>
      </div>
    `
    : '';

  // Slogan - Match PDF format
  const sloganHTML = selectedSlogan?.slogan
    ? `
      <div class="slogan-section">
        <p>${selectedSlogan.slogan}</p>
      </div>
    `
    : '';

  // Final HTML - Match PDF layout exactly
  return `
    <!DOCTYPE html>
    <html lang="${langAttr}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${getTranslation('prescription', language)}</title>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;700&family=Noto+Sans+Gujarati:wght@400;700&display=swap" rel="stylesheet">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        @page {
          size: A4;
          margin: 25mm 15mm 25mm 15mm; /* Increased top/bottom margins for header/footer space */
        }
        
        body {
          font-family: ${fontFamily};
          background: white;
          color: #000;
          line-height: 1.5;
          font-size: 10pt;
          position: relative;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        /* Watermark - Match PDF exactly */
        ${finalIsDuplicate ? `
        body::before {
          content: '${getTranslation('duplicateCopy', language)}';
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-45deg);
          font-size: 68pt;
          font-weight: 700;
          color: rgb(180, 180, 180);
          letter-spacing: 6px;
          white-space: nowrap;
          z-index: 0;
          pointer-events: none;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        ` : ''}

        .content-wrapper {
          position: relative;
          z-index: 1;
        }

        /* Header - Match PDF exactly */
        .header {
          text-align: center;
          border-bottom: 2px solid #000;
          padding: 12pt 0 10pt 0;
          margin-bottom: 15pt;
          background: #fff;
        }

        .hospital-name {
          font-size: 20pt;
          font-weight: bold;
          margin-bottom: 5pt;
        }

        .hospital-address {
          font-size: 10pt;
          font-weight: normal;
        }

        /* Patient & Doctor Details - Match PDF grid layout */
        .details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20pt;
          margin-bottom: 15pt;
        }

        .details-box {
          font-size: 10pt;
        }

        .details-title {
          font-size: 12pt;
          font-weight: bold;
          margin-bottom: 6pt;
        }

        .details-text div {
          margin-bottom: 3pt;
        }

        /* Sections - Match PDF spacing */
        .section {
          margin-bottom: 12pt;
          page-break-inside: avoid;
        }

        .section-title {
          font-size: 11pt;
          font-weight: bold;
          margin-bottom: 4pt;
        }

        .section-content {
          font-size: 10pt;
          margin-left: 30pt;
        }

        /* Two-column inline section for Complaint & Examination (title and content inline) */
        .inline-two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20pt;
          align-items: start;
        }
        .inline-box {
          display: flex;
          gap: 8pt;
          align-items: baseline;
          font-size: 10pt;
        }
        .inline-box .section-title {
          font-size: 11pt;
          font-weight: bold;
          margin: 0;
        }
        .inline-box .section-content {
          margin: 0;
        }

        /* Vitals Grid - Match PDF 2-column layout */
        .vitals-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 5pt;
          margin-left: 30pt;
          font-size: 9pt;
        }

        /* Tables - Match PDF styling exactly */
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 6pt;
          font-size: 10pt;
        }

        th {
          background-color: #f0f0f0 !important;
          font-weight: bold;
          text-align: left;
          padding: 6pt;
          border-bottom: 1px solid #000;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        td {
          padding: 6pt;
          vertical-align: top;
          border-bottom: 1px solid #ddd;
        }

        .test-table td {
          border-bottom: none;
        }

        tr {
          page-break-inside: avoid;
        }

        /* Slogan - Match PDF center styling */
        .slogan-section {
          text-align: center;
          font-style: italic;
          font-size: 10pt;
          margin: 20pt 0;
        }

        /* Footer - Match PDF exact positioning */
        .footer {
          position: fixed;
          bottom: 0;
          right: 0;
          left: 0;
          padding: 20pt 15mm;
        }

        .signature-section {
          text-align: right;
        }

        .signature-label {
          font-weight: bold;
          font-size: 10pt;
          margin-bottom: 5pt;
        }

        .signature-line {
          display: inline-block;
          width: 150pt;
          border-top: 1px solid #000;
          margin-left: 10pt;
        }

        .doctor-name {
          font-size: 9pt;
          margin-top: 5pt;
        }

        .ending-stars {
          text-align: center;
          font-size: 12pt;
          margin: 20pt 0;
        }

        /* Print optimizations */
        @media print {
          body {
            margin: 0;
            padding: 0;
          }

          @page {
            size: A4;
            margin: 30mm 15mm 30mm 15mm; /* Generous margins for header/footer space */
          }

          /* Fixed header at top of every page */
          .header {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            padding: 12pt 15mm 10pt 15mm;
            min-height: 70pt;
            box-sizing: border-box;
            background: #fff;
            z-index: 1000;
            border-bottom: 2px solid #000;
          }

          .content-wrapper {
            /* Push content below the fixed header - must match header height + some buffer */
            padding-top: 80pt;
            padding-bottom: 100pt;
          }

          /* Fixed footer at bottom of every page */
          .footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 20pt 15mm;
            background: #fff;
          }

          ${finalIsDuplicate ? `
          body::before {
            display: block !important;
            opacity: 1 !important;
          }
          ` : ''}
        }
      </style>
    </head>
    <body>
      <div class="content-wrapper">
        <!-- Header -->
        <div class="header">
          <div class="hospital-name">HealthCare Plus Hospital</div>
          <div class="hospital-address">123 Medical Center Drive, Healthcare City | +1-800-HEALTH</div>
        </div>

        <!-- Appointment Details -->
        ${prescriptionData.appointmentDate || prescriptionData.dateTime ? `
        <div class="section" style="margin-bottom: 10pt;">
          <div class="inline-box">
            <div class="section-title">Appointment :</div>
            <div class="section-content">${new Date(prescriptionData.appointmentDate || prescriptionData.dateTime).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</div>
          </div>
        </div>
        ` : ''}

        <!-- Patient & Doctor Details -->
        <div class="details-grid">
          <div class="details-box">
            <div class="details-title">Patient Details :</div>
            <div class="details-text">
              <div>${patientFullName}</div>
              <div>${ageText} / ${resolvedGender}</div>
              <div>Ph: ${phoneDisplay}</div>
              ${patientData?.email ? `<div>Email: ${patientData.email}</div>` : ''}
            </div>
          </div>
          <div class="details-box">
            <div class="details-title">Doctor Details :</div>
            <div class="details-text">
              <div>Dr. ${doctorFullName}</div>
              ${doctorData?.specialization ? `<div>${doctorData.specialization}</div>` : ''}
              ${doctorData?.qualification ? `<div>${doctorData.qualification}</div>` : ''}
              ${doctorData?.licenseNumber ? `<div>Lic: ${doctorData.licenseNumber}</div>` : ''}
              ${doctorData?.registrationNumber ? `<div>Reg: ${doctorData.registrationNumber}</div>` : ''}
            </div>
          </div>
        </div>

        <!-- Content Sections -->
        ${vitalSignsHTML}
        ${complaintExamHTML}
        ${prescriptionData.knownCaseDisease ? `
        <div class="section">
          <div class="inline-box">
            <div class="section-title">Known Disease :</div>
            <div class="section-content" style="white-space: pre-wrap;">${typeof prescriptionData.knownCaseDisease === 'object' ? prescriptionData.knownCaseDisease.name || JSON.stringify(prescriptionData.knownCaseDisease) : prescriptionData.knownCaseDisease}</div>
          </div>
        </div>
        ` : ''}
        ${medicationsHTML}
        ${imagingTestsHTML}
        ${testsHTML}
        ${foodAdviceHTML}
        ${sloganHTML}

        <!-- Ending Stars -->
        <div class="ending-stars">****************</div>
      </div>

      <!-- Footer -->
      <div class="footer">
        <div class="signature-section">
          <div class="signature-label">
            Doctor's Signature
            <span class="signature-line"></span>
          </div>
          <div class="doctor-name">Dr. ${doctorFullName}</div>
        </div>
      </div>
    </body>
    </html>
  `;
};

// Generate PDF from HTML for better Unicode support
const generatePrescriptionPDFFromHTML = async (prescriptionData, patientData, selectedSlogan, isDuplicate = false, options = {}) => {
  try {
    console.log('HTML PDF Generator - isDuplicate:', isDuplicate);

    // Double-check duplicate flag from prescription data as backup
    const duplicateFromData = prescriptionData?.flags?.duplicatePrint;
    const finalIsDuplicate = isDuplicate || duplicateFromData;
    console.log('HTML Generator - Final isDuplicate decision:', finalIsDuplicate);

    const language = prescriptionData.language || 'E';
    const doctorData = options.doctorData || null;
    const doctorFullName = doctorData?.firstName && doctorData?.lastName
      ? `${doctorData.firstName} ${doctorData.lastName}`.trim()
      : doctorData?.name || 'N/A';

    // Resolve patient data fields
    const patientFullName = [patientData?.firstName, patientData?.lastName].filter(Boolean).join(' ').trim() || 'N/A';
    const resolvedGender = formatGenderValue(patientData?.sex, patientData?.gender, patientData?.genderDisplay, patientData?.genderIdentity);
    const phoneDisplay = pickFirstDefined([
      patientData?.mobile,
      patientData?.phone,
      patientData?.contactNumber,
      patientData?.contact?.mobile,
      patientData?.contact?.phone
    ]) || 'N/A';
    const ageText = resolveAgeText(patientData);

    // Create a temporary element with prescription content
    const prescriptionElement = document.createElement('div');
    prescriptionElement.style.position = 'absolute';
    prescriptionElement.style.top = '-9999px';
    prescriptionElement.style.left = '-9999px';
    prescriptionElement.style.width = '210mm'; // A4 width
    prescriptionElement.style.padding = '15mm';
    prescriptionElement.style.backgroundColor = 'white';
    prescriptionElement.style.color = 'black';
    prescriptionElement.style.boxSizing = 'border-box';

    // Set appropriate font for different languages
    let fontFamily = 'Arial, sans-serif';
    if (language === 'H') {
      fontFamily = '"Noto Sans Devanagari", "Mangal", Arial, sans-serif';
    } else if (language === 'M') {
      fontFamily = '"Noto Sans Devanagari", "Shivaji01", Arial, sans-serif';
    } else if (language === 'G') {
      fontFamily = '"Noto Sans Gujarati", "Shruti", Arial, sans-serif';
    }

    prescriptionElement.style.fontFamily = fontFamily;
    prescriptionElement.style.lineHeight = '1.6';

    // Generate HTML content
    prescriptionElement.innerHTML = `
      <div style="text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 15px;">
        <!-- Hospital Header -->
        <div style="margin-bottom: 15px; padding-bottom: 12px; border-bottom: 2px solid #0066cc;">
          <h1 style="color: #0066cc; margin: 0; font-size: 22px; font-family: ${fontFamily};">HealthCare Plus Hospital</h1>
          <p style="color: #666; margin: 5px 0; font-size: 11px; font-family: ${fontFamily};">Excellence in Healthcare | 24/7 Emergency Services</p>
          <p style="color: #666; margin: 5px 0; font-size: 10px; font-family: ${fontFamily};">123 Medical Center Drive, Healthcare City | +1-800-HEALTH | www.healthcareplus.com</p>
        </div>
        
        <h1 style="color: #2563eb; margin: 0; font-size: 24px; font-family: ${fontFamily};">
          ${getTranslation('prescription', language)}
        </h1>
  ${finalIsDuplicate ? '<div style="border: 2px solid rgb(255, 200, 200); padding: 6px 16px; margin: 12px auto; background-color: rgb(255, 245, 245); max-width: 220px; border-radius: 4px; display: inline-block;"><p style="color: rgb(220, 53, 69); font-weight: bold; font-size: 16px; margin: 0; text-align: center; font-family: ' + fontFamily + ';">' + getTranslation('duplicateCopy', language) + '</p></div>' : ''}
        <p style="margin: 5px 0; color: #666; font-family: ${fontFamily}; font-size: 11px;">
          ${getTranslation('date', language)}: ${new Date().toLocaleDateString()}
        </p>
      </div>
      
      ${doctorData ? `
        <div style="margin-bottom: 15px;">
          <h2 style="color: #333; font-size: 16px; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; font-family: ${fontFamily};">
            Doctor Information
          </h2>
          <div style="font-family: ${fontFamily}; font-size: 12px;">
            <p style="margin: 3px 0;"><strong>Dr. ${doctorFullName}</strong></p>
            ${doctorData.qualification ? `<p style="margin: 3px 0;">${doctorData.qualification}</p>` : ''}
            ${doctorData.registrationNumber ? `<p style="margin: 3px 0;">Reg. No: ${doctorData.registrationNumber}</p>` : ''}
          </div>
        </div>
      ` : ''}
      
      <div style="margin-bottom: 15px;">
        <h2 style="color: #333; font-size: 16px; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; font-family: ${fontFamily};">
          ${getTranslation('patientInfo', language)}
        </h2>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px; margin-bottom: 12px; font-family: ${fontFamily}; font-size: 12px;">
          <p style="margin: 3px 0;"><strong>${getTranslation('name', language)}:</strong> ${patientFullName}</p>
          <p style="margin: 3px 0;"><strong>${getTranslation('gender', language)}:</strong> ${resolvedGender}</p>
          <p style="margin: 3px 0;"><strong>${getTranslation('phone', language)}:</strong> ${phoneDisplay}</p>
          <p style="margin: 3px 0;"><strong>${getTranslation('age', language)}:</strong> ${ageText}</p>
        </div>
      </div>

      ${prescriptionData.knownCaseDisease ? `
        <div style="margin-bottom: 15px;">
          <h2 style="color: #333; font-size: 16px; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; font-family: ${fontFamily};">
            Known Case Disease
          </h2>
          <p style="font-family: ${fontFamily}; font-size: 12px; margin: 5px 0;">
            ${typeof prescriptionData.knownCaseDisease === 'object'
          ? `${prescriptionData.knownCaseDisease.name}${prescriptionData.knownCaseDisease.code ? ` (${prescriptionData.knownCaseDisease.code})` : ''}`
          : prescriptionData.knownCaseDisease}
          </p>
        </div>
      ` : ''}
      
      ${(() => {
        const tests = prescriptionData.tests || [];

        if (!tests || tests.length === 0) return '';

        // Extract test names properly from test objects
        const displayTests = tests.map(t => {
          if (typeof t === 'object') {
            return {
              name: t.name || 'Unknown Test',
              code: t.code || ''
            };
          }
          return { name: t, code: '' };
        });

        return `
        <div style="margin-bottom: 15px; page-break-inside: avoid;">
          <h2 style="color: #333; font-size: 16px; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; font-family: ${fontFamily};">
            ${getTranslation('tests', language)}
          </h2>
          <div style="display: flex; flex-wrap: wrap; gap: 6px; font-family: ${fontFamily};">
            ${displayTests.map((test) => {
          return `<span style="background-color: #f3e8ff; color: #6b21a8; border: 1px solid #c084fc; padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 500;">${test.name}${test.code ? ` (${test.code})` : ''}</span>`;
        }).join('')}
          </div>
        </div>
        `;
      })()}
      
      <div style="margin-bottom: 15px;">
        <h2 style="color: #333; font-size: 16px; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; font-family: ${fontFamily};">
          ${getTranslation('medications', language)}
        </h2>
        ${prescriptionData.items.filter(item => {
        // Include item if it has a name directly OR if medicine object has a name
        const hasName = item.name || (typeof item.medicine === 'object' && item.medicine?.name);
        return hasName;
      }).map((item, index) => {
        const medicineName = typeof item?.medicine === 'object' ? item.medicine.name : item?.name || 'N/A';
        const medicineObj = typeof item.medicine === 'object' ? item.medicine : {};
        const frequencyText = item.frequency || '';
        const routeText = item.route || medicineObj.route || 'Oral';
        const daysText = item.days || item.durationDays || medicineObj.days || '';
        const instructionText = item.instruction || medicineObj.instruction || '';
        return `
          <div style="border: 1px solid #ddd; margin-bottom: 10px; padding: 10px; border-radius: 4px; background-color: #f9f9f9; page-break-inside: avoid;">
            <h3 style="margin: 0 0 8px 0; color: #2563eb; font-size: 14px; font-family: ${fontFamily};">
              ${index + 1}. ${medicineName}
            </h3>
            <div style="margin-left: 12px; font-family: ${fontFamily}; font-size: 11px;">
              ${item.dose ? `<p style="margin: 3px 0;"><strong>Dose:</strong> ${item.dose}</p>` : ''}
              ${frequencyText ? `<p style="margin: 3px 0;"><strong>Frequency:</strong> ${frequencyText}</p>` : ''}
              ${routeText ? `<p style="margin: 3px 0;"><strong>Route:</strong> ${routeText}</p>` : ''}
              ${instructionText ? `<p style="margin: 3px 0;"><strong>${getTranslation('instruction', language)}:</strong> ${instructionText}</p>` : ''}
              ${daysText ? `<p style="margin: 3px 0;"><strong>${getTranslation('days', language)}:</strong> ${daysText}</p>` : ''}
            </div>
          </div>
        `}).join('')}
      </div>
      
      ${prescriptionData.foodAdvice ? `
        <div style="margin-bottom: 15px; page-break-inside: avoid;">
          <h2 style="color: #333; font-size: 16px; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; font-family: ${fontFamily};">
            ${getTranslation('foodAdvice', language)}
          </h2>
          <div style="padding: 12px; background-color: #f0fdf4; border-left: 4px solid #22c55e; border-radius: 0 4px 4px 0; font-family: ${fontFamily}; font-size: 12px; white-space: pre-wrap;">
            ${prescriptionData.foodAdvice}
          </div>
        </div>
      ` : ''}
      
      ${selectedSlogan?.slogan ? `
        <div style="margin-top: 25px; margin-bottom: 15px; text-align: left; color: #333; font-size: 12px; font-family: ${fontFamily}; page-break-inside: avoid; font-style: normal;">
          ${selectedSlogan.slogan}
        </div>
      ` : ''}
      
      <div style="margin-top: 35px; border-top: 1px solid #ccc; padding-top: 15px; text-align: right; page-break-inside: avoid;">
        <p style="margin: 5px 0; font-family: ${fontFamily}; font-size: 12px; color: #888;"><strong>${getTranslation('doctorSignature', language)}</strong></p>
        ${doctorData ? `<p style="margin: 5px 0; font-family: ${fontFamily}; font-size: 11px; color: #888;">Dr. ${doctorFullName}</p>` : ''}
        <p style="margin: 5px 0; color: #888; font-family: ${fontFamily};">
          <span style="border-bottom: 1px solid #aaa; display: inline-block; width: 120px; margin-left: 8px;"></span>
        </p>
        <p style="margin-top: 5px; font-size: 9px; color: #888; font-style: italic; font-family: ${fontFamily};">
          Generated: ${new Date().toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        })}
        </p>
      </div>
    `;

    // Add to document temporarily
    document.body.appendChild(prescriptionElement);

    // Wait for fonts to load
    await new Promise(resolve => setTimeout(resolve, 500));

    // Generate canvas from HTML
    const canvas = await html2canvas(prescriptionElement, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      onclone: (clonedDoc) => {
        // Ensure fonts are loaded in cloned document
        const clonedElement = clonedDoc.body.querySelector('div');
        if (clonedElement) {
          clonedElement.style.fontFamily = fontFamily;
        }
      }
    });

    // Remove temporary element
    document.body.removeChild(prescriptionElement);

    // --- Improved multi-page rendering to avoid header/footer overlap ---
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const headerHeightMM = 30; // reserved visual header area (pages 2+)
    const footerHeightMM = 30; // reserved footer area (all pages)
    const headerHeightPx = Math.floor((headerHeightMM / pdfWidth) * canvas.width); // convert using horizontal scale
    const footerHeightPx = Math.floor((footerHeightMM / pdfWidth) * canvas.width);
    // mm per px horizontally = pdfWidth / canvas.width; use that to derive slice heights
    const mmPerPx = pdfWidth / canvas.width;
    const page1SlicePx = Math.floor((pdfHeight - footerHeightMM) / mmPerPx);
    const otherSlicePx = Math.floor((pdfHeight - headerHeightMM - footerHeightMM) / mmPerPx);

    const makeSlice = (startPx, heightPx) => {
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = heightPx;
      const ctx = sliceCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, startPx, canvas.width, heightPx, 0, 0, canvas.width, heightPx);
      return sliceCanvas.toDataURL('image/png');
    };

    const drawWatermark = () => {
      if (!finalIsDuplicate) return;
      const text = getTranslation('duplicateCopy', language);
      pdf.saveGraphicsState();
      // Darker, bold watermark for duplicate prints
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(90, 90, 90);
      pdf.setFontSize(90);
      try {
        pdf.text(text, 18, pdfHeight + pdfWidth * -0.25, { angle: 45 });
      } catch { pdf.text(text, pdfWidth / 2, pdfHeight / 2, { align: 'center' }); }
      pdf.restoreGraphicsState();
    };

    const drawHeader = () => {
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 86, 179);
      pdf.text('HealthCare Plus Hospital', pdfWidth / 2, 15, { align: 'center' });
      pdf.setDrawColor(0, 86, 179);
      pdf.setLineWidth(0.4);
      pdf.line(15, 20, pdfWidth - 15, 20);
    };

    const drawFooter = (pageNum) => {
      const footerY = pdfHeight - 20;
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(15, footerY, pdfWidth - 15, footerY);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      const ts = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      pdf.text(`Generated on: ${ts}`, 15, footerY + 8);
      pdf.text("Doctor's Signature", pdfWidth - 15, footerY + 8, { align: 'right' });
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Dr. ${doctorFullName}`, pdfWidth - 15, footerY + 14, { align: 'right' });
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Page ${pageNum}`, pdfWidth / 2, pdfHeight - 5, { align: 'center' });
    };

    let offsetPx = 0;
    let pageNum = 1;
    // Page 1 (HTML already includes its own header so we place slice at y=0)
    let sliceHeightPx = Math.min(page1SlicePx, canvas.height - offsetPx);
    drawWatermark();
    const firstImg = makeSlice(offsetPx, sliceHeightPx);
    pdf.addImage(firstImg, 'PNG', 0, 0, pdfWidth, (sliceHeightPx * pdfWidth) / canvas.width);
    drawFooter(pageNum);
    offsetPx += sliceHeightPx;

    // Subsequent pages
    while (offsetPx < canvas.height - 1) {
      pageNum++;
      pdf.addPage();
      drawWatermark();
      drawHeader();
      sliceHeightPx = Math.min(otherSlicePx, canvas.height - offsetPx);
      const pageImg = makeSlice(offsetPx, sliceHeightPx);
      pdf.addImage(pageImg, 'PNG', 0, headerHeightMM, pdfWidth, (sliceHeightPx * pdfWidth) / canvas.width);
      drawFooter(pageNum);
      offsetPx += sliceHeightPx;
    }

    // Generate filename
    const firstName = (patientData?.firstName || 'patient').replace(/\s+/g, '_');
    const lastName = (patientData?.lastName || '').replace(/\s+/g, '_');
    const patientName = lastName ? `${firstName}_${lastName}` : firstName;
    const fileName = `prescription_${patientName}_${finalIsDuplicate ? 'DUPLICATE_' : ''}${new Date().toISOString().split('T')[0]}.pdf`;

    // Save PDF
    pdf.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating PDF from HTML:', error);
    return { success: false, error: error.message };
  }
};

export { getTranslation, languageMap };